import { css, cx } from '@emotion/css';
import { useState, type HTMLAttributes } from 'react';
import { CreditCard, Calendar, Lock, User } from 'lucide-react';
import { getPaymentMethodIdFromCardNumber } from '../../utils/payment.utils';

const formStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-lg)',
});

const formGroup = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-xs)',
});

const formRow = css({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 'var(--spacing-md)',
  '@media (max-width: 768px)': {
    gridTemplateColumns: '1fr',
  },
});

const label = css({
  fontSize: 'var(--fs-350)',
  color: 'var(--g-700)',
  fontWeight: 500,
});

const inputWrapper = css({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
});

const icon = css({
  position: 'absolute',
  left: 'var(--spacing-sm)',
  width: 'var(--fs-500)',
  height: 'var(--fs-500)',
  color: 'var(--g-400)',
});

const input = css({
  width: '100%',
  padding: 'var(--spacing-sm) var(--spacing-sm) var(--spacing-sm) calc(var(--spacing-xl) + var(--spacing-xs))',
  fontSize: 'var(--fs-350)',
  border: '1px solid var(--g-300)',
  borderRadius: 'var(--spacing-xs)',
  outline: 'none',
  transition: 'all 0.2s ease',
  '&:focus': {
    borderColor: 'var(--pc-600)',
    boxShadow: '0 0 0 2px rgba(var(--pcrgba-600), 0.2)',
  },
  '&::placeholder': {
    color: 'var(--g-400)',
  },
});

const saveCardCheckbox = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-sm)',
  marginTop: 'var(--spacing-xs)',
  '& input[type="checkbox"]': {
    width: '18px',
    height: '18px',
    minWidth: '18px',
    cursor: 'pointer',
    accentColor: 'var(--pc-600)',
  },
  '& label': {
    cursor: 'pointer',
    fontSize: 'var(--fs-350)',
    color: 'var(--g-700)',
    userSelect: 'none',
  },
});

// Format helpers
const formatCardNumber = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');
  const formatted = cleaned.replace(/(\d{4})(?=\d)/g, '$1 ');
  return formatted;
};

const formatExpiry = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length >= 2) {
    return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}`;
  }
  return cleaned;
};

export interface DebitCardData {
  cardNumber: string;
  cardholderName: string;
  expirationMonth: string;
  expirationYear: string;
  securityCode: string;
  saveCard: boolean;
  cardToken?: string;
  paymentMethodId?: string; // e.g., 'debmaster' or provider id returned by MP for debit
}

export interface PaymentMethodDebitCardProps extends HTMLAttributes<HTMLDivElement> {
  onDataChange?: (data: DebitCardData, isValid: boolean) => void;
}

export function PaymentMethodDebitCard({
  onDataChange,
  className,
  style,
  ...props
}: PaymentMethodDebitCardProps) {
  const [cardNumber, setCardNumber] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [saveCard, setSaveCard] = useState(false);
  const [isGeneratingToken, setIsGeneratingToken] = useState(false);

  const validateAndNotify = async (data: Partial<DebitCardData>) => {
    const currentData: DebitCardData = {
      cardNumber: data.cardNumber ?? cardNumber,
      cardholderName: data.cardholderName ?? cardholderName,
      expirationMonth: data.expirationMonth ?? (expiry.split('/')[0] || ''),
      expirationYear: data.expirationYear ?? (expiry.split('/')[1] || ''),
      securityCode: data.securityCode ?? cvv,
      saveCard: data.saveCard ?? saveCard,
    };

    const baseValid =
      currentData.cardNumber.replace(/\D/g, '').length >= 13 &&
      currentData.cardholderName.trim().length > 0 &&
      currentData.expirationMonth.length === 2 &&
      currentData.expirationYear.length === 2 &&
      currentData.securityCode.length >= 3;

    // Optionally generate token (not required by backend for debit). We still try to resolve method id.
    if (baseValid && !isGeneratingToken) {
      setIsGeneratingToken(true);
      try {
        // Not required; commented out to avoid confusion
        // const token = await generateCardToken({
        //   cardNumber: currentData.cardNumber.replace(/\D/g, ''),
        //   cardholderName: currentData.cardholderName,
        //   cardExpirationMonth: currentData.expirationMonth,
        //   cardExpirationYear: `20${currentData.expirationYear}`,
        //   securityCode: currentData.securityCode,
        // });
        // currentData.cardToken = token.id;

        // Resolve debit payment method id from BIN
        const methodId = await getPaymentMethodIdFromCardNumber(currentData.cardNumber, 'debit_card');
        if (methodId) currentData.paymentMethodId = methodId;
      } catch (error) {
        console.error('Error generating card token:', error);
      } finally {
        setIsGeneratingToken(false);
      }
    }

    onDataChange?.(currentData, baseValid);
  };

  const handleCardNumberChange = (value: string) => {
    const formatted = formatCardNumber(value);
    setCardNumber(formatted);
    validateAndNotify({ cardNumber: formatted });
  };

  const handleExpiryChange = (value: string) => {
    const formatted = formatExpiry(value);
    setExpiry(formatted);
    const [month, year] = formatted.split('/');
    validateAndNotify({
      expirationMonth: month || '',
      expirationYear: year || '',
    });
  };

  return (
    <div className={cx(formStyles, className)} style={style} {...props}>
      <div className={formGroup}>
        <label className={label} htmlFor="debitCardNumber">
          Número do Cartão
        </label>
        <div className={inputWrapper}>
          <CreditCard className={icon} />
          <input
            type="text"
            id="debitCardNumber"
            placeholder="0000 0000 0000 0000"
            maxLength={19}
            value={cardNumber}
            onChange={(e) => handleCardNumberChange(e.target.value)}
            className={input}
          />
        </div>
      </div>

      <div className={formGroup}>
        <label className={label} htmlFor="debitCardholderName">
          Nome no Cartão
        </label>
        <div className={inputWrapper}>
          <User className={icon} />
          <input
            type="text"
            id="debitCardholderName"
            placeholder="Nome como está no cartão"
            value={cardholderName}
            onChange={(e) => {
              setCardholderName(e.target.value);
              validateAndNotify({ cardholderName: e.target.value });
            }}
            className={input}
          />
        </div>
      </div>

      <div className={formRow}>
        <div className={formGroup}>
          <label className={label} htmlFor="debitExpiry">
            Validade
          </label>
          <div className={inputWrapper}>
            <Calendar className={icon} />
            <input
              type="text"
              id="debitExpiry"
              placeholder="MM/AA"
              maxLength={5}
              value={expiry}
              onChange={(e) => handleExpiryChange(e.target.value)}
              className={input}
            />
          </div>
        </div>

        <div className={formGroup}>
          <label className={label} htmlFor="debitCvv">
            CVV
          </label>
          <div className={inputWrapper}>
            <Lock className={icon} />
            <input
              type="text"
              id="debitCvv"
              placeholder="123"
              maxLength={4}
              value={cvv}
              onChange={(e) => {
                const cleaned = e.target.value.replace(/\D/g, '');
                setCvv(cleaned);
                validateAndNotify({ securityCode: cleaned });
              }}
              className={input}
            />
          </div>
        </div>
      </div>

      <div className={saveCardCheckbox}>
        <input
          type="checkbox"
          id="saveDebitCard"
          checked={saveCard}
          onChange={(e) => {
            setSaveCard(e.target.checked);
            validateAndNotify({ saveCard: e.target.checked });
          }}
        />
        <label htmlFor="saveDebitCard">Salvar cartão para compras futuras</label>
      </div>
    </div>
  );
}

export default PaymentMethodDebitCard;
